import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '@/lib/db/db';
import { useAthleteStore } from '@/store';
import { ExperienceLevel } from '@/types';
import { ChevronLeft } from 'lucide-react';

export function CreateProfilePage() {
    const navigate = useNavigate();
    const { setActiveId } = useAthleteStore();

    const [name, setName] = useState('');
    const [level, setLevel] = useState<ExperienceLevel>('Beginner');
    const [years, setYears] = useState(0);
    const [avatar, setAvatar] = useState('🤼');

    const avatars = ['🤼', '🦁', '🐯', '🦅', '🦈', '🐻', '🐺', '🦍', '⚡', '🔥'];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const id = `ath_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        await db.athletes.add({
            id,
            name,
            level,
            experienceYears: years,
            avatar,
            createdAt: new Date().toISOString()
        });

        await setActiveId(id);
        navigate('/');
    };

    return (
        <div className="min-h-screen bg-bg flex flex-col items-center justify-center p-6 animate-fade-in">
            <div className="w-full max-w-md space-y-6">
                <div className="flex items-center gap-4 mb-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-text-muted hover:text-white transition-colors"
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <h1 className="text-2xl font-bold text-white">New Profile</h1>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Avatar Selection */}
                    <div>
                        <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-3">Choose Avatar</label>
                        <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
                            {avatars.map(char => (
                                <button
                                    key={char}
                                    type="button"
                                    onClick={() => setAvatar(char)}
                                    className={`w-12 h-12 rounded-xl text-2xl flex items-center justify-center border transition-all ${avatar === char ? 'bg-gold-base border-gold-base shadow-lg scale-110' : 'bg-bg-elevated border-white/10 hover:bg-white/5'}`}
                                >
                                    {char}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Name */}
                    <div>
                        <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Wrestler Name</label>
                        <input
                            required
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            className="w-full bg-bg-elevated border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-gold-base transition-colors"
                            placeholder="e.g. Jordan"
                        />
                    </div>

                    {/* Experience Level */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Experience Level</label>
                            <select
                                value={level}
                                onChange={e => setLevel(e.target.value as ExperienceLevel)}
                                className="w-full bg-bg-elevated border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-gold-base transition-colors appearance-none"
                            >
                                <option value="Beginner">Beginner</option>
                                <option value="Intermediate">Intermediate</option>
                                <option value="Advanced">Advanced</option>
                                <option value="Elite">Elite</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Years Wrestling</label>
                            <input
                                type="number"
                                min="0"
                                max="50"
                                value={years}
                                onChange={e => setYears(Number(e.target.value))}
                                className="w-full bg-bg-elevated border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-gold-base transition-colors"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="w-full py-4 rounded-xl bg-gold-base text-bg-900 font-bold text-lg shadow-[0_4px_20px_rgba(212,175,55,0.3)] hover:shadow-[0_4px_28px_rgba(212,175,55,0.45)] transition-all active:scale-[0.98] mt-4"
                    >
                        Create Profile
                    </button>
                </form>
            </div>
        </div>
    );
}
